import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { EducationalHistories, CareerHistories, PreferredOccupationItem, OccupationItem } from '../../../types/personShow';
import { getSalaryRangeText } from '../../../libs/salaryUtils';

type Props = {
  id: string;
  title: string;
  penIconImg: string;
  type: string;
  value?: string | string[] | {
    name: {
      url: string;
    };
    extension?: string;
  };
  array?: string[] | EducationalHistories[] | CareerHistories[] | OccupationItem[] | PreferredOccupationItem[];
  word: string;
  updatedAt?: string;
  personId?: number;
  isShow: boolean;
  unCompleted?: boolean;
  last?: boolean;
  isStudent?: boolean;
  isMiddle?: boolean;
  isNotRequested?: boolean;
  employmentTypeMap: { [k: string]: string; };
  onClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  onClickHistoriesChild: (event: React.MouseEvent<HTMLDivElement>) => void;
  onRequestResume: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

export const Presentation: FunctionComponent<Props> = (props) => {
  const preferenceConditionsRef = useRef<HTMLParagraphElement>(null);
  const [isPreferenceConditionsTruncated, setIsPreferenceConditionsTruncated] = useState(false);
  const preferenceConditionsText = props.type === 'preferenceConditions' && Array.isArray(props.array)
    ? (props.array as string[]).join(' / ')
    : '';

  useEffect(() => {
    if (props.type !== 'preferenceConditions') return;

    const updateTruncatedState = () => {
      const element = preferenceConditionsRef.current;
      setIsPreferenceConditionsTruncated(
        element !== null && element.scrollWidth > element.clientWidth
      );
    };

    updateTruncatedState();
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updateTruncatedState);
    if (preferenceConditionsRef.current) {
      resizeObserver?.observe(preferenceConditionsRef.current);
    }
    window.addEventListener('resize', updateTruncatedState);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateTruncatedState);
    };
  }, [props.type, preferenceConditionsText]);

  const isNotLastItem = (array: any[], target: any) => {
    if (array.length === 1) return false;
    return array.length !== array.indexOf(target) + 1;
  };

  const isSelectionEmpty = () => {
    if (Array.isArray(props.value)) {
      return props.value.length === 0;
    }
    return !props.value;
  };

  const renderContent = () => {
    switch (props.type) {
      case 'skills':
      case 'brands': {
        const items = props.array as string[] | undefined;
        return (
          <div className={`skills ${items && items.length === 0 ? 'normal' : ''}`}>
            {items?.map((child, index) => (
              <p className="skillTitle" key={`${child}_${index}`}>{child}</p>
            ))}
            {(items?.length === 0 || items === undefined) && <div className="selectable">{props.word}</div>}
          </div>
        );
      }
      case 'histories':
        if (props.id === 'educationalHistories') {
          const educationalHistories = props.array as EducationalHistories[];
          return (
            <>
              {educationalHistories?.map((object, index) =>
                <div className={`histories ${!props.isShow ? 'disabled' : ''}`} key={`${object.name}_${index}`} onClick={props.onClickHistoriesChild}>
                  <p className="semiTitle">
                    <span>{object.name}</span>
                    <img src={props.penIconImg} alt="ペンアイコン画像" className="pen" />
                  </p>
                  <div className="row">
                    <p className="time">{object.department}</p>
                    <p className="time">{object.startDate} ~ {object.endDate}</p>
                  </div>
                  {object.description !== '' && object.description !== null &&
                    <div className="description">
                      {object.description}
                    </div>
                  }
                </div>
              )}
              {props.array?.length === 0 &&
                <div className="selectable">{props.word}</div>
              }
              {props.isShow && props.type === 'histories' && props.array && props.array.length !== 0 &&
                <div className="histories__coverBtn">
                  <div onClick={props.onClickHistoriesChild} className="histories__addBtn">
                    <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.03847 4.96152H0.5V4.03848H4.03847V0.5H4.96152V4.03848H8.5V4.96152H4.96152V8.5H4.03847V4.96152Z" fill="white" />
                    </svg>
                    追加
                  </div>
                </div>
              }
            </>
          );
        } else if (props.id === 'careerHistories') {
          const careerHistories = props.array as CareerHistories[];
          return (
            <>
              {careerHistories?.map((object, index) =>
                <div className={`histories ${!props.isShow ? 'disabled' : ''}`} key={`${object.name}_${index}`} onClick={props.onClickHistoriesChild}>
                  <p className="semiTitle">
                    <span>{object.name}</span>
                    <img src={props.penIconImg} alt="ペンアイコン画像" className="pen" />
                  </p>
                  <p className="time margin">
                    {[object.careerEmploymentTypeId != null ? props.employmentTypeMap[object.careerEmploymentTypeId] : undefined, object.department, `${object.startDate} ~ ${object.currentWorking ? '現在' : object.endDate}`].filter(Boolean).join(' / ')}
                  </p>
                  <div className="row margin">
                    {object.position.length > 0 && object.position.map((occupation) =>
                      <p className="department">{occupation.occupationName}{occupation.occupationDetailName !== '' && occupation.occupationDetailName !== null ? `：${occupation.occupationDetailName}` : ''}</p>
                    )}
                  </div>
                  <div className="row">
                    {object.salaryRange != null && String(object.salaryRange) !== '' && (
                      <p className="time">{getSalaryRangeText(object.salaryRange)}</p>
                    )}
                  </div>
                  {object.description !== '' && object.description !== null &&
                    <div className="description">
                      {object.description}
                    </div>
                  }
                </div>
              )}
              {props.array && props.array.length === 0 &&
                <div className="selectable">{props.word}</div>
              }
              {props.isShow && props.type === 'histories' && props.array && props.array.length !== 0 &&
                <div className="histories__coverBtn">
                  <div onClick={props.onClickHistoriesChild} className="histories__addBtn">
                    <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.03847 4.96152H0.5V4.03848H4.03847V0.5H4.96152V4.03848H8.5V4.96152H4.96152V8.5H4.03847V4.96152Z" fill="white" />
                    </svg>
                    追加
                  </div>
                </div>
              }
            </>
          );
        }
        break;
      case 'location': {
        const items = props.array as string[] | undefined;
        return (
          <>
            <div className="location">
              {Array.isArray(items) && items.map((child, index) => (
                <React.Fragment key={`${child}_${index}`}>
                  <p className="name">{child}</p>
                  {isNotLastItem(items, child) && <strong>,</strong>}
                </React.Fragment>
              ))}
            </div>
            {(items?.length === 0 || items === undefined) && <div className="selectable">{props.word}</div>}
          </>
        );
      }
      case 'preferenceConditions': {
        const items = props.array as string[] | undefined;
        return (
          <>
            {props.isShow ? (
              <>
                {Array.isArray(items) && items.length > 0 && (
                  <div className="preferenceConditions">
                    <p ref={preferenceConditionsRef} className="preferenceConditions__items">
                      {preferenceConditionsText}
                    </p>
                    {isPreferenceConditionsTruncated && (
                      <p className="preferenceConditions__count">{items.length}項目を選択中</p>
                    )}
                  </div>
                )}
                {(items?.length === 0 || items === undefined) && (
                  <div className="selectable">{props.word}</div>
                )}
              </>
            ) : (
              items && Object.keys(items).length > 0 && (
                <section>
                  <div className="preference">
                    {Object.entries(items).map(
                      ([categoryName, conditions]) => (
                        <div key={categoryName}>
                          <p>{categoryName}</p>
                          {Array.isArray(conditions) && (
                            <span>{conditions.map((condition) => condition.name).join(', ')}</span>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </section>
              )
            )}
          </>
        );
      }
      case 'occupations': {
        if (!Array.isArray(props.array)) {
          return <div className="selectable">{props.word}</div>;
        }
        const occupations = props.array as OccupationItem[];
        return (
          <>
            {occupations?.map((object, index) => (
              <div className="occupations" key={`${object.occupation}_${index}`}>
                <div>
                  <p>・{object.occupation}</p>
                  {object.occupationDetail && (
                    <>
                      <strong>：</strong>
                      <p>{object.occupationDetail}</p>
                    </>
                  )}
                </div>
              </div>
            ))}

            {occupations.length === 0 && (
              <div className="selectable">{props.word}</div>
            )}
          </>
        );
      }
      case 'resume':
        return (
          <>
            {props.value !== '' && (
              <div className="resume">
                <div className="resume__upload">
                  <p className="resume__upload--title">アップロード済</p>
                  <p className="resume__upload--time">最終アップロード日：{props.updatedAt}</p>
                </div>
                <div className="resume__btns">
                  {/* indeed経由で応募した方は履歴書の拡張子がpdf以外の可能性もあるので、条件分岐を追加 */}
                  {props.id === 'resume' &&
                    typeof props.value === 'object' &&
                    props.value !== null &&
                    'name' in props.value &&
                    typeof props.value.name?.url === 'string' &&
                    props.value.extension?.toLowerCase() === '.pdf' && (
                      <div className="coverBtn">
                        <a onClick={e => e.stopPropagation()} href={`/users/${props.personId}/preview_resume`} target="_blank" className="resume__btns--preview">プレビュー</a>
                      </div>
                    )}
                  {props.id === 'job_resume' &&
                    typeof props.value === 'object' &&
                    props.value !== null &&
                    'name' in props.value &&
                    typeof props.value.name?.url === 'string' && (
                      <div className="coverBtn">
                        <a onClick={e => e.stopPropagation()} href={`/users/${props.personId}/preview_job_resume`} target="_blank" className="resume__btns--preview">プレビュー</a>
                      </div>
                    )}
                  {!props.isShow && (
                    <div className="coverBtn">
                      <a onClick={e => e.stopPropagation()} href={`/users/${props.personId}/${props.id === 'resume' ? 'download_resume' : 'download_job_resume'}`} target="_blank" className="resume__btns--preview download">ダウンロード</a>
                    </div>
                  )}
                  {props.isShow && (
                    <div className="coverBtn">
                      <a onClick={e => e.stopPropagation()} href={`/format/${props.personId}`} className="resume__btns--new">作成</a>
                    </div>
                  )}
                </div>
              </div>
            )}
            {props.value === '' && (
              props.isShow ? (
                <div className="selectable resume">
                  {props.word}
                  <div className="coverBtn">
                    <a onClick={e => e.stopPropagation()} href={`/format/${props.personId}`} className="resume__btns--new">作成</a>
                  </div>
                </div>
              ) : (
                <div className="resume request">
                  <p className="resume__upload--title">まだアップロードされていません</p>
                  <div className="coverBtn">
                    <a className={`resume__btns--request${!props.isNotRequested ? ' disabled' : ''}`} onClick={props.isNotRequested ? props.onRequestResume : undefined}>
                      <span className="text">{props.isNotRequested ? 'リクエストを送信' : 'リクエスト送信済み'}</span>
                      {props.isNotRequested && <span className="tooltip">{props.id === 'resume' ? '履歴書のアップロードを促せます' : '職務経歴書のアップロードを促せます'}</span>}
                    </a>
                  </div>
                </div>
              )
            )}
          </>
        );
      default:
        return (
          <>
            {typeof props.value === 'string' && props.value !== '' && (
              <div className="normal">
                <div dangerouslySetInnerHTML={{ __html: props.value.replace(/\n/g, '<br />') }} />
              </div>
            )}
            {isSelectionEmpty() && <div className="selectable">{props.word}</div>}
          </>
        );
    }
  };

  return (
    <div
      id={props.id}
      className={`person__show__main__item ${props.last ? 'last' : ''} ${!props.isShow ? 'disabled' : ''} ${props.isMiddle ? 'middle' : ''} ${props.type === 'histories' && props.array && props.array.length !== 0 ? 'disabledHover' : ''}`}
      onClick={props.onClick}
    >
      <div className="person__show__main__item--heading">
        <div className="flex">
          <p className="title">{props.title}</p>
          {props.unCompleted && !props.isStudent && (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.99962 11.9838C8.16667 11.9838 8.30404 11.9301 8.41173 11.8226C8.51942 11.7152 8.57326 11.5779 8.57326 11.4109C8.57326 11.2438 8.51955 11.1065 8.41211 10.9988C8.30467 10.8911 8.16743 10.8373 8.00038 10.8373C7.83333 10.8373 7.69596 10.891 7.58827 10.9984C7.48058 11.1059 7.42673 11.2431 7.42673 11.4102C7.42673 11.5772 7.48045 11.7146 7.58789 11.8223C7.69533 11.93 7.83257 11.9838 7.99962 11.9838ZM7.56924 9.01377H8.52467V3.76033H7.56924V9.01377ZM8.00701 16C6.89887 16 5.86099 15.79 4.89338 15.3701C3.92577 14.9502 3.07837 14.3768 2.35116 13.6499C1.62395 12.923 1.05028 12.0754 0.63017 11.1072C0.210057 10.139 0 9.10021 0 7.99088C0 6.88695 0.209966 5.84931 0.629897 4.87795C1.04983 3.90659 1.62323 3.06082 2.35011 2.34064C3.077 1.62044 3.92456 1.05028 4.89279 0.630171C5.86102 0.210058 6.89979 0 8.00912 0C9.11305 0 10.1507 0.209966 11.1221 0.629897C12.0934 1.04983 12.9392 1.61972 13.6594 2.33959C14.3796 3.05946 14.9497 3.90598 15.3698 4.87915C15.7899 5.8523 16 6.89025 16 7.99299C16 9.10113 15.79 10.139 15.3701 11.1066C14.9502 12.0742 14.3803 12.9203 13.6604 13.645C12.9405 14.3696 12.094 14.9432 11.1209 15.3659C10.1477 15.7886 9.10975 16 8.00701 16Z" fill="#FF3B30" />
            </svg>
          )}
        </div>
        {(props.type !== 'histories' || (props.type === 'histories' && props.array?.length === 0)) && (
          <img src={props.penIconImg} alt="ペンアイコン画像" className="pen" />
        )}
      </div>
      <div className="person__show__main__item--content">{renderContent()}</div>
    </div>
  );
};
